import * as yaml from 'js-yaml'
import { JSONPath } from "jsonpath-plus"
import * as lodash from 'lodash'
import * as _ from "lodash"
import { Action, DiffResult, ExecuteContext, K8SResourceType, KV, Operator, PlanContext, PlanContextData } from "yam-types"
import { ExtractMergedHandlerOpFnDiffType, MergedHandler } from "../engine/composer"
import TemplateRender from "../template/render"
import { createLogger, normalizeJsonPath } from "../util"
/**
 * Plan stage context data and methods, used by plugins
 */
export default class YamPlanContext implements PlanContext {

  data: PlanContextData

  log = createLogger('plan')

  _ = lodash

  private templateRender: TemplateRender

  constructor(templateRender: TemplateRender, data: PlanContextData) {
    this.templateRender = templateRender
    this.data = data
    if (data.runMode !== "uninstall") {
      this.generateInitActions(data)
    }
  }

  /**
   * Append function to operation action queue
   * 
   * @param actionFunc the functions that need to be executed during 'apply' stage
   */
  public action(actionFunc: Action, name: string, thisArg?: Operator<unknown>): void {
    if (thisArg) {
      // wrap thisArg for class style operator
      const wrap = async (ctx: ExecuteContext) => {
        await actionFunc.call(thisArg, ctx)
      }
      wrap.actionName = name
      this.data.actions.push(wrap)
    } else {
      this.data.actions.push(actionFunc)
    }
    if (name) {
      (actionFunc as unknown as KV).actionName = name
    }
    this.log.info(`action [${name || actionFunc.name || '<anonymous>'}] added`)
  }

  public async renderTemplate(relativePath: string, handleInclude: boolean): Promise<string> {
    return this.templateRender.renderTemplate(this.data.workingDir, relativePath, handleInclude, this.data.customizedValues)
  }

  public async loadFile(relativePath: string): Promise<string | undefined> {
    if (!relativePath) {
      return
    }
    const content = await this.templateRender.checkAndReadFile(this.data.workingDir, relativePath)
    this.log.info(`read raw content from file: ${relativePath}`)
    return content
  }

  public async diffJsonPathAndInvokeHandlers(jsonPathHandlers: MergedHandler[]): Promise<void> {
    for (const matchedHandler of jsonPathHandlers) {
      const jsonPath = normalizeJsonPath(matchedHandler.matcher)
      const partialCurrent = JSONPath({ path: jsonPath, json: this.data.currentModelFull }) || []
      const partialPrevious = JSONPath({ path: jsonPath, json: this.data.previousModelFull }) || []
      this.log.info(`fetch and diff partial objects of JSON path '${matchedHandler.matcher}'`)
      const diff = this.calculateDiff<ExtractMergedHandlerOpFnDiffType<typeof matchedHandler>>(partialPrevious, partialCurrent, jsonPath)
      this.log.info(`---------------------------------------------`)

      // call operate function of all plugins to compose the plan stage
      for (const operateFunction of matchedHandler.handlers) {
        this.log.info(`invoke plugin [${operateFunction.pluginName}]`)
        const actionCnt = this.data.actions.length
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        await operateFunction.func.call({}, this, diff)
        this.log.info(`plugin [${operateFunction.pluginName}] invoked, match expr '${matchedHandler.matcher}', [${this.data.actions.length - actionCnt}] actions appended.`)
        this.log.info(`---------------------------------------------\n`)
      }
    }
  }

  private calculateDiff<T>(previous: T[], current: T[], jsonPath?: string): DiffResult<T> {
    const currentMap = this.convertSelectedItemsToMap<T>(current)
    const previousMap = this.convertSelectedItemsToMap<T>(previous)

    const diff: DiffResult<T> = {
      currentItems: [...currentMap.values()],

      hasDiff: false,

      hasNew: false,
      hasDeleted: false,
      hasModified: false,

      newItems: [],
      deletedItems: [],
      modifiedItems: []
    }
    currentMap.forEach((val, key) => {
      if (!previousMap.has(key)) {
        diff.hasNew = true
        diff.newItems.push(val)
        this.log.info(`[+] new item detected, identifier: ${key}`)
      } else {
        if (!_.isEqual(val, previousMap.get(key))) {
          diff.hasModified = true
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          diff.modifiedItems.push({ previous: previousMap.get(key)!, current: val })
          this.log.info(`[~] modified item detected, identifier: ${key}`)
        }
        previousMap.delete(key)
      }
    })
    previousMap.forEach((val, key) => {
      diff.hasDeleted = true
      diff.deletedItems.push(val)
      this.log.info(`[-] deleted item detected, identifier: ${key}`)
    })
    diff.hasDiff = diff.hasDeleted || diff.hasNew || diff.hasModified
    if (!diff.hasDiff) {
      this.log.info(`[=] no diff detected for object path: ${jsonPath}`)
    }
    return diff
  }

  private convertSelectedItemsToMap<T>(arr: T[]): Map<string, T> {
    const result = new Map<string, T>()
    for (const index in arr) {
      const item = arr[index]
      if (item instanceof Array) {
        throw new Error(`nested array not allowed: \n${yaml.dump(item)}`)
      }
      if (item === null || item === undefined) {
        continue
      }
      if (typeof item === "object" && item['name' as keyof T]) {
        // 'name' is the conventional identifier for the object
        result.set((item['name' as keyof T]) as unknown as string, item)
      } else {
        // basic types or object that has no identifier key, put index as map key
        result.set("#" + index, item)
      }
    }
    return result
  }

  private generateInitActions(ctxData: PlanContextData) {
    if (ctxData.dryRun) {
      return
    }

    // add init namespace action
    const namespace = ctxData.namespace
    const checkNamespace: Action = async (ctx: ExecuteContext): Promise<void> => {
      if (!await ctx.k8sClient.namespaceExists(namespace)) {
        await ctx.k8sClient.apply({
          apiVersion: "v1",
          kind: K8SResourceType.Namespace,
          metadata: {
            name: namespace
          }
        })
        ctx.log.info(`new namespace '${namespace}' created.`)
      }
    }
    this.action(checkNamespace, 'check-init-namespace:' + namespace)
  }

}